import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { always } from 'ol/events/condition';
import { containsExtent, getCenter, getWidth } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import { Point } from 'ol/geom';
import DragBox from 'ol/interaction/DragBox';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';

import {
	clearFindRun,
	findSegment,
	followFindRun,
	getFindSession,
	getLatestFindRun,
	modelBody,
	queryBody,
	saveFindRun,
	similarBody,
	startFindRun,
	startFindSession,
	stopFindRun,
	stopFindSession,
	streamOutlines,
} from '@utils/api/find';
import { roundedBox } from '@utils/map/similarity/similarityBox';
import { SIMILARITY_Z_INDEX } from '@utils/map/similarity/similaritySource';
import { colormapColor, withAlpha } from '@utils/map/similarity/similarityStyle';
import { invalidateCollectionResults } from '@utils/collectionQueries';

import { dropIds } from './findSave';
import { isSegmentResult, segmentLook } from './findSegment';
import { searchProgress, searchReducer } from './searchState';
import { OBJECT_COLORS } from './useSimilarityMap';
import { CHECKBOX, checkboxCentre, checkboxSize } from './useSearchGrid';

const geojson = new GeoJSON();

// A result as a box on the map, by its id (kept as the results stream in)
const resultFeature = result => {
	const feature = geojson.readFeature({ type: 'Feature', geometry: result.geometry });
	feature.setId(result.id);
	feature.set('bbox', result.bbox);
	feature.set('result', result);
	return feature;
};

// Box looks on the map, in pixels and alpha: checked boxes solid, unchecked thinner and fainter,
// removed ones a faint dashed trace (still clickable, to bring back), unfused previews dashed.
// Every box keeps a little fill, so a click anywhere inside it hits it. Hovered and selected boxes
// get a soft glow.
const BOX = {
	checked: { width: 1.5, alpha: 1, fill: 0.12 },
	unchecked: { width: 1.5, alpha: 0.75, fill: 0.06 },
	accepted: { width: 2.5, alpha: 1, fill: 0.12 },
	rejected: { width: 1, alpha: 0.45, fill: 0.02, dash: [4, 4] },
	preview: { width: 1, alpha: 0.5, fill: 0.02, dash: [3, 3] },
	glow: [
		{ width: 10, alpha: 0.12 },
		{ width: 5, alpha: 0.3 },
	],
};

const boxLook = (decision, { preview, kept }) => {
	if (decision) return BOX[decision];
	if (preview) return BOX.preview;
	if (kept === true) return BOX.checked;
	return kept === false ? BOX.rejected : BOX.unchecked;
};

// "Tick all in view" ticks only squares the user can judge: wholly on screen, at a zoom where a
// typical box is at least this wide, in screen pixels
const TICK_MIN_BOX_PX = 8;

// How often to ask about compute, by its state: often while it starts, once a minute while ready
// (to notice when it stops itself), every 10 s while it finishes stopping (about 6.5 min, F02),
// never while stopped or failed. Asking is not use: compute stops itself 10 minutes after the last
// search, outline or example, also while the panel stays open.
const SESSION_POLL_MS = { starting: 3000, ready: 60000, stopping: 10000 };
// Start compute when the panel opens, for administrators (who may start it; later Enterprise, or by
// credits). Only then: once it has stopped itself, it stays off until someone asks.
const AUTO_START = true;
// What a user who may not start compute (403) is told instead
const ASK_ADMIN = 'Ask an administrator to start compute.';

// A run's events stream cut off (the network dropped, the server restarted): followed again from the
// last event seen, after each of these waits in turn, before the panel says it failed
const RESUME_DELAYS_MS = [1000, 2000, 4000, 8000, 8000];
// Worth following again: not a failed run (`final`), nor a request refused (4xx)
const resumable = error => !error?.final && !(error?.status >= 400 && error?.status < 500);
// Resolves true after `ms`, or false as soon as `signal` aborts
const wait = (ms, signal) =>
	new Promise(resolve => {
		if (signal.aborted) {
			resolve(false);
			return;
		}
		const timer = setTimeout(() => resolve(true), ms);
		signal.addEventListener(
			'abort',
			() => {
				clearTimeout(timer);
				resolve(false);
			},
			{ once: true }
		);
	});

// "Outline all" sends this many boxes per request, and shows what came back at most this often
const OUTLINE_BATCH = 300;
const OUTLINE_FLUSH_MS = 250;

export const VERDICT = { TICKED: 'ticked', CLEARED: 'cleared' };

// A click on the heatmap is outlined within a box this many times the examples' size around it, so
// a click off the object's centre still holds it
const PICK_BOX_SCALE = 1.5;

// Two boxes are one object when they overlap by IoU >= 0.3, or one lies at least 0.7 inside the
// other (a van and its end): the backend's rule (F05, `find_service.same_object`)
const SAME_IOU = 0.3;
const SAME_INSIDE = 0.7;
const boxArea = b => (b[2] - b[0]) * (b[3] - b[1]);
const intersection = (a, b) =>
	Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0])) *
	Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
const sameObject = (a, b) => {
	const inter = intersection(a, b);
	const union = boxArea(a) + boxArea(b) - inter;
	const smaller = Math.min(boxArea(a), boxArea(b));
	return (
		(union > 0 && inter / union >= SAME_IOU) || (smaller > 0 && inter / smaller >= SAME_INSIDE)
	);
};
// `outer` is the larger box and holds `inner` (the whole van, `inner` its end)
const holds = (outer, inner) =>
	boxArea(outer) > boxArea(inner) && intersection(outer, inner) / boxArea(inner) >= SAME_INSIDE;

// A box's size: its longer side in ground metres (EPSG:3857 lengths are inflated by cosh(y / R))
const EARTH_RADIUS = 6378137;
export const boxSize = ([x0, y0, x1, y1]) =>
	Math.max(x1 - x0, y1 - y0) / Math.cosh((y0 + y1) / 2 / EARTH_RADIUS);

// The size filter starts at the object's usual size (Claude's, or measured from the examples) divided
// and multiplied by this; boxes outside are hidden until the range is widened
export const SIZE_SPAN = 2.5;
// Boxes the user added are never hidden by size
const ADDED = ['manual', 'click'];

// A run's saved job from its document: `saved` when the server sends it, else its `saved_job_uuid`
const savedOf = run =>
	run?.saved ??
	(run?.saved_job_uuid
		? { job_uuid: run.saved_job_uuid, count: run.saved_count ?? null, labels: null }
		: null);

const median = values => {
	if (values.length === 0) return null;
	const sorted = [...values].sort((a, b) => a - b);
	return sorted[Math.floor(sorted.length / 2)];
};

/**
 * "Find" (proof of concept): a text search or "find all like these", streamed square by square, as
 * boxes on the map; outlines of clicked objects; square by square review in local state (saving
 * comes with Objects).
 *
 * @param {Object} params
 * @param {Object|null} params.mapObject
 * @param {string} [params.projectUuid]
 * @param {Object} params.token - antd theme token: `similarityColormap`, accent and outline colours
 * @param {boolean} params.open - Whether the Find panel is open (starts and keeps compute)
 * @param {boolean} params.active - Whether map clicks are for Find (not while marking an example)
 * @param {Object|null} [params.pickOnClick] - While the heatmap shows: `{ group, label, size }` of
 *   the object a click on the map adds (SAM 3 outlines what is under it; `size` [w, h] in map units is
 *   its examples'). Null: a click selects a square
 * @param {Function} params.searchArea - Where the next search runs: `{ bounds, polygon }` (polygon:
 *   a drawn area's ring, or undefined for the view)
 * @param {Function} params.onError - Called with a failed request's error
 * @param {boolean} [params.autoStart] - Start compute when the panel opens (administrators only)
 */
export const useFindResults = ({
	mapObject,
	projectUuid,
	token,
	open,
	active,
	pickOnClick = null,
	searchArea,
	onError,
	autoStart = false,
}) => {
	// Read when called, so effects (the map's clicks, "Add missed") do not bind again on every render
	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;
	const reportError = useCallback(error => onErrorRef.current?.(error), []);
	const [search, dispatchSearch] = useReducer(searchReducer, null);
	// id → 'accepted' or 'rejected'
	const [decisions, setDecisions] = useState({});
	const [outline, setOutline] = useState(null);
	// "Outline all": result id → outline geometry, and how far it got
	const [outlines, setOutlines] = useState({});
	const [outlining, setOutlining] = useState(null);
	// The result under the pointer, on the map or in the list, and the last one clicked
	const [hoveredId, setHoveredId] = useState(null);
	const [selectedId, setSelectedId] = useState(null);
	// Square by square review: plan tile index → VERDICT, and the square selected
	const [tileReview, setTileReview] = useState({});
	const [selectedTile, setSelectedTile] = useState(null);
	// The squares "Tick all in view" would tick now, or null when zoomed out too far to judge boxes
	const [tickableInView, setTickableInView] = useState([]);
	const abortRef = useRef(null);
	const outlineAbortRef = useRef(null);
	const colors = token.similarityColormap;
	const allResults = useMemo(() => Object.values(search?.byId ?? {}), [search?.byId]);
	// The size filter, per object: [min, max] ground metres set by hand; until then the usual size's range
	const [sizeRanges, setSizeRanges] = useState({});
	const expectedSizes = useMemo(
		() => (search?.plan?.sizes ?? []).map(size => size?.length_m ?? null),
		[search?.plan?.sizes]
	);
	const sizeRange = useCallback(
		group => {
			if (sizeRanges[group]) return sizeRanges[group];
			const expected = expectedSizes[group];
			return expected ? [expected / SIZE_SPAN, expected * SIZE_SPAN] : null;
		},
		[sizeRanges, expectedSizes]
	);
	const fitsSize = useCallback(
		result => {
			const range = sizeRange(result.group);
			if (!range || !result.bbox || result.sources.some(source => ADDED.includes(source)))
				return true;
			const size = boxSize(result.bbox);
			return size >= range[0] && size <= range[1];
		},
		[sizeRange]
	);
	// What the map, the counts and the review see: the boxes within their object's size range
	const results = useMemo(() => allResults.filter(fitsSize), [allResults, fitsSize]);
	// Per object, for the size filter's row: every box's size, the range, and how many it hides
	const sizeGroups = useMemo(() => {
		const groups = {};
		allResults.forEach(result => {
			if (result.preview || !result.bbox) return;
			const entry = groups[result.group] ?? {
				group: result.group,
				label: result.label,
				expected: expectedSizes[result.group] ?? null,
				range: sizeRange(result.group),
				sizes: [],
				hidden: 0,
			};
			entry.sizes.push(boxSize(result.bbox));
			if (!fitsSize(result)) entry.hidden += 1;
			groups[result.group] = entry;
		});
		return Object.values(groups).sort((a, b) => a.group - b.group);
	}, [allResults, expectedSizes, sizeRange, fitsSize]);
	// A search for several objects ("car, house", or examples of each): boxes take their object's colour
	const multiObject = useMemo(
		() => (search?.labels?.length ?? 0) > 1 || new Set(results.map(r => r.group)).size > 1,
		[search?.labels, results]
	);
	const planTiles = useMemo(() => search?.plan?.tiles ?? [], [search?.plan]);
	// Squares in the order the keys step through them: nearest the searched area's centre first
	const tileOrder = useMemo(() => {
		if (!search?.plan) return [];
		const [cx, cy] = getCenter(search.plan.searched_bounds);
		const distance = ({ core }) => {
			const [x, y] = getCenter(core);
			return (x - cx) ** 2 + (y - cy) ** 2;
		};
		return [...planTiles].sort((a, b) => distance(a) - distance(b));
	}, [search?.plan, planTiles]);

	// The run on the server this page shows. A search runs on the server, so it goes on when the page
	// goes away (another tab of the app, a reload); the page only follows it.
	const runRef = useRef(null);
	// The same, for rendering (Save needs a run)
	const [runUuid, setRunUuid] = useState(null);
	const setRun = useCallback(uuid => {
		runRef.current = uuid;
		setRunUuid(uuid);
	}, []);
	// Bumped by every new search, clear and close: what comes back for an older one is dropped, and a
	// run started for it is cleared (no page shows it)
	const generationRef = useRef(0);
	// The generation whose run is being started: a second Find meanwhile (Enter and a click) is the same
	const startingRef = useRef(null);
	const resetReview = () => {
		setDecisions({});
		setSelectedId(null);
		setOutlines({});
		setOutlining(null);
		setTileReview({});
		setSelectedTile(null);
		setSizeRanges({});
	};
	// A run that ended: its cost and saved job, from its document (the last event may not say)
	const loadMeta = runUuid =>
		Promise.resolve()
			.then(() => getLatestFindRun({ projectUuid }))
			.then(latest => {
				if (latest?.uuid !== runUuid || runRef.current !== runUuid) return;
				dispatchSearch({ type: 'meta', cost: latest.cost, saved: savedOf(latest) });
			})
			.catch(() => null);
	// Follow a run's events after `after`; a page leaving stops the following, not the run. The run's
	// past (all of a finished run) is applied in one go, up to `caught_up`; then each event as it comes.
	// A stream cut off is followed again from the last event seen, a few times (RESUME_DELAYS_MS).
	const follow = async (runUuid, after) => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		let lastSeq = after;
		let past = [];
		const settle = () => {
			if (past?.length) dispatchSearch({ type: 'batch', events: past });
			past = null;
		};
		const onEvent = event => {
			if (event.type === 'ping') return;
			if (Number.isInteger(event.seq)) lastSeq = event.seq;
			if (event.type === 'caught_up') settle();
			else if (past) past.push(event);
			else dispatchSearch(event);
		};
		for (let attempt = 0; ; attempt += 1) {
			try {
				const last = await followFindRun(
					{ projectUuid, runUuid, after: lastSeq },
					{ onEvent, signal: controller.signal }
				);
				settle();
				if (last && !last.cost) loadMeta(runUuid);
				return last;
			} catch (error) {
				if (controller.signal.aborted) return null;
				if (resumable(error) && attempt < RESUME_DELAYS_MS.length) {
					if (await wait(RESUME_DELAYS_MS[attempt], controller.signal)) continue;
					return null;
				}
				settle();
				dispatchSearch({ type: 'error', message: error.message });
				reportError(error);
				return null;
			}
		}
	};
	// One search at a time: a new one clears the one before, on the server too
	const run = async (kind, body) => {
		if (startingRef.current === generationRef.current) return null;
		generationRef.current += 1;
		const generation = generationRef.current;
		startingRef.current = generation;
		abortRef.current?.abort();
		outlineAbortRef.current?.abort();
		if (runRef.current)
			clearFindRun({ projectUuid, runUuid: runRef.current }).catch(() => null);
		setRun(null);
		resetReview();
		dispatchSearch({ type: 'start', kind, request: body });
		let started;
		try {
			started = await startFindRun({ projectUuid, kind, body });
			if (!started?.uuid) throw new Error('The search did not start');
		} catch (error) {
			if (generation !== generationRef.current) return null;
			dispatchSearch({ type: 'error', message: error.message });
			reportError(error);
			return null;
		} finally {
			if (startingRef.current === generation) startingRef.current = null;
		}
		// Cleared, closed or followed by another search while it started: nobody shows it, so it goes
		if (generation !== generationRef.current) {
			clearFindRun({ projectUuid, runUuid: started.uuid }).catch(() => null);
			return null;
		}
		setRun(started.uuid);
		dispatchSearch(started.plan);
		return follow(started.uuid, 0);
	};
	useEffect(
		() => () => {
			abortRef.current?.abort();
			outlineAbortRef.current?.abort();
		},
		[]
	);

	// Opening Find picks up the user's latest search where it is: running on, or finished while
	// the page was away. Not over a search the panel shows already, unless that one failed.
	const searchShown = useRef(false);
	searchShown.current = Boolean(search && !search.error);
	useEffect(() => {
		if (!open || !projectUuid) return undefined;
		let alive = true;
		const generation = generationRef.current;
		getLatestFindRun({ projectUuid })
			.then(latest => {
				if (!alive || !latest || searchShown.current) return;
				if (generation !== generationRef.current) return;
				setRun(latest.uuid);
				dispatchSearch({
					type: 'start',
					kind: latest.kind,
					request: latest.request,
					cost: latest.cost,
					saved: savedOf(latest),
				});
				follow(latest.uuid, -1);
			})
			.catch(() => null);
		return () => {
			alive = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- once per opening of the panel
	}, [open, projectUuid]);

	// Stop a running search; what the finished squares found stays. The run finishes its square and
	// then says `stopped`, which the following shows; until then the panel says "Stopping…".
	const stop = useCallback(() => {
		const runUuid = runRef.current;
		if (!runUuid) {
			// Still starting: once started, it is cleared
			generationRef.current += 1;
			abortRef.current?.abort();
			dispatchSearch({ type: 'stopped' });
			return;
		}
		dispatchSearch({ type: 'stopping' });
		stopFindRun({ projectUuid, runUuid }).catch(error => {
			// Not heard: let go of the run, which may never say it stopped
			if (runRef.current !== runUuid) return;
			abortRef.current?.abort();
			dispatchSearch({ type: 'stopped' });
			reportError(error);
		});
	}, [projectUuid, reportError]);

	// Compute: asked every few seconds while it starts, and every minute while it is ready and the
	// panel is open. It starts when the panel opens.
	const session = useQuery({
		queryKey: ['find', 'session', projectUuid],
		queryFn: () => getFindSession({ projectUuid }),
		enabled: Boolean(projectUuid && open),
		refetchInterval: query => SESSION_POLL_MS[query.state.data?.state] ?? false,
		refetchIntervalInBackground: true,
	});
	const queryClient = useQueryClient();
	const [startRequestedAt, setStartRequestedAt] = useState(null);
	const startSession = useMutation({
		mutationFn: () => startFindSession({ projectUuid }),
		onMutate: () => setStartRequestedAt(Date.now()),
		onSuccess: data => queryClient.setQueryData(['find', 'session', projectUuid], data),
		// Only administrators may start compute (403): not an expired login, and not "Find failed"
		onError: error => reportError(error?.status === 403 ? new Error(ASK_ADMIN) : error),
	});
	const stopSession = useMutation({
		mutationFn: () => stopFindSession({ projectUuid }),
		onSuccess: data => queryClient.setQueryData(['find', 'session', projectUuid], data),
		onError: reportError,
	});
	const workerState = session.data?.state;
	const { mutate: startWorker, isPending: startPending } = startSession;
	// A start is due: the panel just opened (AUTO_START, administrators), or someone asked while compute was still
	// stopping (it cannot start before it has stopped). It is done once compute runs, so stopping
	// itself later never starts it again.
	const [startQueued, setStartQueued] = useState(false);
	useEffect(() => {
		setStartQueued(open && AUTO_START && autoStart);
	}, [open, autoStart]);
	// Decided on a fresh answer only: the one kept from the panel's last opening may be stale
	const sessionFetching = session.isFetching;
	useEffect(() => {
		if (!open || !startQueued || sessionFetching) return;
		if (workerState === 'ready' || workerState === 'starting') setStartQueued(false);
		else if (workerState === 'stopped' && !startPending) {
			setStartQueued(false);
			startWorker();
		}
	}, [open, startQueued, sessionFetching, workerState, startWorker, startPending]);
	// "Start compute": now, or as soon as it has finished stopping
	const requestStart = useCallback(() => {
		if (workerState === 'stopping') setStartQueued(true);
		else startWorker();
	}, [workerState, startWorker]);
	useEffect(() => {
		if (workerState === 'ready' || workerState === 'stopped') setStartRequestedAt(null);
	}, [workerState]);
	// The kinds of run each search makes: words naming roads or rivers make a segment run
	const status = (...kinds) => ({
		isPending: Boolean(search?.running && kinds.includes(search.kind)),
		isSuccess: Boolean(
			search && !search.running && !search.error && kinds.includes(search.kind)
		),
	});
	const query = {
		...status('query', 'segment'),
		mutate: text => run('query', queryBody({ text, ...searchArea() })),
	};
	// "Find all like these": boxes around every object like the examples, at their sizes
	const similar = {
		...status('similar'),
		mutateAsync: ({ groups }) => run('similar', similarBody({ groups, ...searchArea() })),
	};
	// A trained model over the area, as a detection job runs it: the model's own squares, merged at
	// the end
	const model = {
		...status('model'),
		mutate: modelUuid => run('model', modelBody({ modelUuid, ...searchArea() })),
	};

	// "Save results": the run's boxes, without those the user took out (`dropIds`), as a job of the
	// project, or into a collection's objects. Saving again gives the same job.
	const save = useMutation({
		mutationFn: async ({ runUuid: savedRun, name, collectionUuid }) => {
			if (!savedRun) throw new Error('There is no search to save.');
			const drop = dropIds({
				results: allResults,
				decisions,
				tileReview,
				hidden: result => !fitsSize(result),
			});
			return saveFindRun({ projectUuid, runUuid: savedRun, name, drop, collectionUuid });
		},
		onSuccess: (saved, { runUuid: savedRun }) => {
			if (runRef.current === savedRun) dispatchSearch({ type: 'saved', saved });
			queryClient.invalidateQueries({ queryKey: ['find', 'saved', projectUuid] });
			// Into a collection: its analyzed area, counts and results on the map changed
			if (saved?.collection) {
				queryClient.invalidateQueries({ queryKey: ['find', 'collections', projectUuid] });
				invalidateCollectionResults(queryClient);
			}
		},
	});

	// "Outline all": an outline for every result not rejected, filled in as they come: OUTLINE_BATCH
	// boxes a request, the outlines shown at most every OUTLINE_FLUSH_MS (not one render each)
	const outlineAll = async () => {
		outlineAbortRef.current?.abort();
		const controller = new AbortController();
		outlineAbortRef.current = controller;
		const targets = results.filter(
			r => r.bbox && effectiveDecisions[r.id] !== 'rejected' && !r.preview
		);
		const found = {};
		let done = 0;
		let timer = null;
		const flush = () => {
			clearTimeout(timer);
			timer = null;
			if (controller.signal.aborted) return;
			setOutlines({ ...found });
			setOutlining(current => current && { ...current, done });
		};
		setOutlines({});
		setOutlining({ done: 0, total: targets.length });
		try {
			for (let first = 0; first < targets.length; first += OUTLINE_BATCH) {
				const batch = targets.slice(first, first + OUTLINE_BATCH);
				await streamOutlines(
					{ projectUuid, boxes: batch.map(r => r.bbox) },
					{
						signal: controller.signal,
						onEvent: event => {
							if (event.type !== 'outline') return;
							const id = batch[event.i]?.id;
							if (id && event.feature?.geometry) found[id] = event.feature.geometry;
							done = first + event.i + 1;
							timer ??= setTimeout(flush, OUTLINE_FLUSH_MS);
						},
					}
				);
			}
		} catch (error) {
			if (!controller.signal.aborted) reportError(error);
		}
		flush();
		if (!controller.signal.aborted)
			setOutlining(current => current && { ...current, finished: true });
	};

	// Each box's decision, from the hand decisions (`decisions`) and its square's verdict: removed by
	// hand stays removed whatever the square says; a cleared square removes the rest of its boxes; a
	// ticked square keeps them
	const effectiveDecisions = useMemo(() => {
		const out = {};
		results.forEach(result => {
			if (result.preview) return;
			const hand = decisions[result.id];
			const verdict = tileReview[result.tile];
			if (hand === 'rejected' || (verdict === VERDICT.CLEARED && hand !== 'accepted')) {
				out[result.id] = 'rejected';
			} else if (hand === 'accepted' || verdict === VERDICT.TICKED) {
				out[result.id] = 'accepted';
			}
		});
		return out;
	}, [results, decisions, tileReview]);

	// Tick a square (its boxes are right, and it holds no others) or clear it (it holds none of the
	// Object). Doing the same again undoes it. Clearing also drops boxes kept by hand in it.
	const reviewTile = useCallback(
		(tile, verdict) => {
			if (tile === null || tile === undefined) return;
			const next = tileReview[tile] === verdict ? undefined : verdict;
			setTileReview(current => ({ ...current, [tile]: next }));
			if (next === VERDICT.CLEARED) {
				setDecisions(current => {
					const out = { ...current };
					results
						.filter(result => result.tile === tile && out[result.id] === 'accepted')
						.forEach(result => delete out[result.id]);
					return out;
				});
			}
		},
		[tileReview, results]
	);

	// A click on a box: remove it, or bring a removed one back. Bringing one back in a cleared square
	// makes that square ticked with only this box kept.
	const toggleRemoved = useCallback(
		result => {
			if (effectiveDecisions[result.id] !== 'rejected') {
				setDecisions(current => ({ ...current, [result.id]: 'rejected' }));
				return;
			}
			if (tileReview[result.tile] === VERDICT.CLEARED) {
				setTileReview(current => ({ ...current, [result.tile]: VERDICT.TICKED }));
				setDecisions(current => {
					const out = { ...current };
					results
						.filter(other => other.tile === result.tile && !other.preview)
						.forEach(other => {
							out[other.id] = 'rejected';
						});
					delete out[result.id];
					return out;
				});
				return;
			}
			setDecisions(current => {
				const out = { ...current };
				delete out[result.id];
				return out;
			});
		},
		[effectiveDecisions, tileReview, results]
	);

	const tickAllInView = useCallback(() => {
		const verdicts = Object.fromEntries(
			tickableInView.filter(i => !tileReview[i]).map(i => [i, VERDICT.TICKED])
		);
		setTileReview(current => ({ ...current, ...verdicts }));
	}, [tickableInView, tileReview]);

	const untickAll = useCallback(() => setTileReview({}), []);

	// Which squares "Tick all in view" may tick, as the map moves: wholly on screen, and only at a
	// zoom where a typical box is at least TICK_MIN_BOX_PX wide
	useEffect(() => {
		if (!mapObject || search?.running || planTiles.length === 0) {
			setTickableInView([]);
			return undefined;
		}
		const boxWidth = median(results.filter(r => r.bbox).map(r => getWidth(r.bbox)));
		const update = () => {
			const view = mapObject.getView();
			if (boxWidth && boxWidth / view.getResolution() < TICK_MIN_BOX_PX) {
				setTickableInView(null);
				return;
			}
			const extent = view.calculateExtent(mapObject.getSize());
			setTickableInView(
				planTiles.filter(({ core }) => containsExtent(extent, core)).map(({ i }) => i)
			);
		};
		update();
		mapObject.on('moveend', update);
		return () => mapObject.un('moveend', update);
	}, [mapObject, search?.running, planTiles, results]);

	// Select a square, panning to it when it is not wholly on screen
	const selectTile = useCallback(
		index => {
			setSelectedTile(index);
			const tile = planTiles.find(({ i }) => i === index);
			if (!mapObject || !tile) return;
			const view = mapObject.getView();
			if (!containsExtent(view.calculateExtent(mapObject.getSize()), tile.core)) {
				view.animate({ center: getCenter(tile.core), duration: 250 });
			}
		},
		[mapObject, planTiles]
	);

	// Keys: ← and → step through the squares, Space ticks the selected one, D clears it, Escape lets
	// go. On window in the capture phase, so the map's own keyboard panning does not also run.
	useEffect(() => {
		if (tileOrder.length === 0 || search?.running) return undefined;
		const handleKey = event => {
			if (event.target.closest?.('input, textarea, [contenteditable="true"]')) return;
			const at = tileOrder.findIndex(({ i }) => i === selectedTile);
			if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
				const step = event.key === 'ArrowRight' ? 1 : -1;
				const next = at < 0 ? 0 : (at + step + tileOrder.length) % tileOrder.length;
				selectTile(tileOrder[next].i);
			} else if (event.key === ' ' && selectedTile !== null) {
				reviewTile(selectedTile, VERDICT.TICKED);
			} else if (event.key.toLowerCase() === 'd' && selectedTile !== null) {
				reviewTile(selectedTile, VERDICT.CLEARED);
			} else if (event.key === 'Escape' && selectedTile !== null) {
				setSelectedTile(null);
			} else {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
		};
		window.addEventListener('keydown', handleKey, true);
		return () => window.removeEventListener('keydown', handleKey, true);
	}, [tileOrder, selectedTile, search?.running, reviewTile, selectTile]);

	const segment = useMutation({
		mutationFn: target => findSegment({ projectUuid, ...target }),
		onSuccess: feature => setOutline(feature?.geometry ?? null),
		onError: reportError,
	});

	// One layer for every search: as results stream in, only boxes new by id are added and those gone
	// removed (a box whose result changed is updated in place); hover and decisions only restyle it
	// Drawn in the results' order (best first, as before), not the order the boxes came in
	const drawOrderRef = useRef(new Map());
	const resultsLayer = useMemo(
		() =>
			new VectorLayer({
				source: new VectorSource(),
				zIndex: SIMILARITY_Z_INDEX + 3,
				renderOrder: (a, b) =>
					(drawOrderRef.current.get(a.getId()) ?? 0) -
					(drawOrderRef.current.get(b.getId()) ?? 0),
			}),
		[]
	);
	useEffect(() => {
		const source = resultsLayer.getSource();
		const wanted = new Map(results.map(result => [result.id, result]));
		drawOrderRef.current = new Map(results.map((result, k) => [result.id, k]));
		const gone = source.getFeatures().filter(feature => !wanted.has(feature.getId()));
		if (gone.length && gone.length === source.getFeatures().length) source.clear(true);
		else gone.forEach(feature => source.removeFeature(feature));
		const added = [];
		wanted.forEach((result, id) => {
			const feature = source.getFeatureById(id);
			if (!feature) {
				added.push(resultFeature(result));
			} else if (feature.get('result') !== result) {
				if (feature.get('result').geometry !== result.geometry) {
					feature.setGeometry(geojson.readGeometry(result.geometry));
				}
				feature.setProperties({ bbox: result.bbox, result });
			}
		});
		if (added.length) source.addFeatures(added);
		resultsLayer.changed();
	}, [resultsLayer, results]);

	// On the map while there are results
	const hasResults = results.length > 0;
	useEffect(() => {
		if (!mapObject || !hasResults) return undefined;
		mapObject.addLayer(resultsLayer);
		return () => mapObject.removeLayer(resultsLayer);
	}, [mapObject, resultsLayer, hasResults]);

	// Rounded boxes on the score ramp; kept ones in the accent colour. Under the pointer a box says
	// what a click does: red with ✕ (remove), or white with ↺ for a removed one (bring back).
	// The styles are made once per look and shared by the boxes; each box's rounded outline is made
	// once per zoom level (`rounded`), not on every frame.
	useEffect(() => {
		const radius = token.borderRadiusLG;
		const rounded = new WeakMap();
		const outlineOf = feature => rounded.get(feature)?.geometry;
		const cornerOf = feature => rounded.get(feature)?.corner;
		const styles = new Map();
		const cached = (key, make) => {
			if (!styles.has(key)) styles.set(key, make());
			return styles.get(key);
		};
		const badge = (text, color) =>
			new Style({
				geometry: cornerOf,
				image: new CircleStyle({
					radius: token.sizeSM,
					fill: new Fill({ color }),
					stroke: new Stroke({ color: token.colorBgMask, width: 1 }),
				}),
				text: new Text({
					text,
					font: `${token.fontWeightStrong} ${token.fontSizeSM}px ${token.fontFamily}`,
					fill: new Fill({ color: token.colorWhite }),
				}),
			});
		const hoveredLook = removed => {
			const color = removed ? token.colorWhite : token.colorError;
			return [
				new Style({
					geometry: outlineOf,
					stroke: new Stroke({ color: withAlpha(color, 0.35), width: 6 }),
				}),
				new Style({
					geometry: outlineOf,
					stroke: new Stroke({ color, width: 2, lineDash: removed ? [4, 4] : undefined }),
					fill: new Fill({ color: withAlpha(color, 0.15) }),
				}),
				badge(removed ? '↺' : '✕', removed ? token.colorPrimary : token.colorError),
			];
		};
		const boxStyles = (color, look, glow) => [
			...(glow
				? BOX.glow.map(
						({ width, alpha }) =>
							new Style({
								geometry: outlineOf,
								stroke: new Stroke({ color: withAlpha(color, alpha), width }),
							})
					)
				: []),
			new Style({
				geometry: outlineOf,
				stroke: new Stroke({
					color: withAlpha(color, look.alpha),
					width: look.width,
					lineDash: look.dash,
				}),
				fill: new Fill({ color: withAlpha(color, look.fill) }),
			}),
		];
		// A traced piece (segment mode): an area's translucent fill and thin outline, or a centreline
		// over a dark halo, dashed where a gap was bridged; in its label's colour
		const segmentStyles = (color, look) =>
			look.type === 'line'
				? [
						new Style({
							stroke: new Stroke({
								color: withAlpha(token.colorBgMask, look.haloAlpha),
								width: look.halo,
								lineDash: look.dash,
							}),
						}),
						new Style({
							stroke: new Stroke({ color, width: look.width, lineDash: look.dash }),
						}),
					]
				: new Style({
						stroke: new Stroke({
							color: withAlpha(color, look.outlineAlpha),
							width: look.outline,
						}),
						fill: new Fill({ color: withAlpha(color, look.fill) }),
					});
		resultsLayer.setStyle((feature, resolution) => {
			const result = feature.get('result');
			if (isSegmentResult(result)) {
				const color = OBJECT_COLORS[result.group % OBJECT_COLORS.length];
				const look = segmentLook(result);
				return cached(`segment|${color}|${look.type}|${look.dash ?? ''}`, () =>
					segmentStyles(color, look)
				);
			}
			const { id, score } = result;
			const decision = effectiveDecisions[id];
			const hovered = id === hoveredId && !result.preview;
			const geometry = feature.getGeometry();
			const known = rounded.get(feature);
			if (known?.resolution !== resolution || known.source !== geometry) {
				const extent = geometry.getExtent();
				rounded.set(feature, {
					resolution,
					source: geometry,
					geometry: roundedBox(extent, radius * resolution),
					corner: new Point([extent[2], extent[3]]),
				});
			}
			if (hovered) {
				const removed = decision === 'rejected';
				return cached(`hovered|${removed}`, () => hoveredLook(removed));
			}
			const look = boxLook(decision, result);
			// Several objects: each in its own colour, kept or not (a kept box is the thicker one)
			const color = multiObject
				? OBJECT_COLORS[result.group % OBJECT_COLORS.length]
				: decision === 'accepted'
					? token.colorPrimary
					: colormapColor(colors, score);
			const glow = decision !== 'rejected' && id === selectedId;
			const key = [color, look.width, look.alpha, look.fill, look.dash, glow].join('|');
			return cached(key, () => boxStyles(color, look, glow));
		});
	}, [resultsLayer, effectiveDecisions, hoveredId, selectedId, colors, token, multiObject]);

	// The outline of the last clicked object, and those of "Outline all": white with a faint dark
	// halo, to read on any ground
	const allOutlines = useMemo(
		() => [...(outline ? [outline] : []), ...Object.values(outlines)],
		[outline, outlines]
	);
	useEffect(() => {
		if (!mapObject || allOutlines.length === 0) return undefined;
		const layer = new VectorLayer({
			source: new VectorSource({
				features: allOutlines.map(geometry =>
					geojson.readFeature({ type: 'Feature', geometry })
				),
			}),
			style: [
				new Style({ stroke: new Stroke({ color: token.colorBgMask, width: 4 }) }),
				new Style({
					stroke: new Stroke({ color: token.colorWhite, width: 2 }),
					fill: new Fill({ color: token.colorFillTertiary }),
				}),
			],
			zIndex: SIMILARITY_Z_INDEX + 4,
		});
		mapObject.addLayer(layer);
		return () => mapObject.removeLayer(layer);
	}, [mapObject, allOutlines, token.colorBgMask, token.colorWhite, token.colorFillTertiary]);

	// Clicks on the map, answered at once (`click`, not `singleclick`, which waits out a possible
	// double click; a double click on a box removes and restores it, so it only zooms):
	// - a square's checkbox ticks the square, or undoes that;
	// - a box is removed, or a removed one comes back;
	// - with the heatmap showing, anywhere else adds the object under the click (`pick`);
	// - otherwise, anywhere else in a square selects the square.
	// The pointer is a hand over anything clickable; hovering a box highlights its row.
	const { mutate: runSegment } = segment;

	// "Add missed": a box the search missed, as one of its objects. A click lets SAM 3 find the box of
	// the object under it; a drag is taken as drawn. An added box counts as kept. A click on the
	// heatmap adds the same way (`pickOnClick`).
	const [adding, setAdding] = useState(null); // { group, label } while adding
	const [addingBusy, setAddingBusy] = useState(false);
	const addedRef = useRef(0);
	const startAdding = useCallback(
		(group = 0) => {
			const label =
				search?.labels?.[group] ??
				results.find(r => r.group === group)?.label ??
				search?.name ??
				'object';
			setAdding({ group, label });
		},
		[search?.labels, search?.name, results]
	);
	const stopAdding = useCallback(() => setAdding(null), []);
	// A box the user added, as one of the search's objects ({ group, label }), kept
	const addFound = useCallback(
		(box, { group, label }, source) => {
			const [x, y] = getCenter(box);
			const cell = planTiles.find(
				({ core }) => core[0] <= x && x < core[2] && core[1] <= y && y < core[3]
			);
			addedRef.current += 1;
			const id = `m-${addedRef.current}`;
			const [x0, y0, x1, y1] = box;
			dispatchSearch({
				type: 'add',
				feature: {
					type: 'Feature',
					geometry: {
						type: 'Polygon',
						coordinates: [
							[
								[x0, y0],
								[x1, y0],
								[x1, y1],
								[x0, y1],
								[x0, y0],
							],
						],
					},
					properties: {
						id,
						bbox: box,
						label,
						group,
						score: 1,
						sources: [source],
						kept: true,
						tile: cell?.i ?? null,
					},
				},
			});
			setDecisions(current => ({ ...current, [id]: 'accepted' }));
		},
		[planTiles]
	);
	const addMissed = useCallback(
		box => {
			if (adding) addFound(box, adding, 'manual');
		},
		[adding, addFound]
	);
	useEffect(() => {
		if (!mapObject || !active || !adding) return undefined;
		const viewport = mapObject.getViewport();
		viewport.style.cursor = 'crosshair';
		const drag = new DragBox({ condition: always });
		drag.on('boxend', () => addMissed(drag.getGeometry().getExtent()));
		mapObject.addInteraction(drag);
		const handleClick = async event => {
			setAddingBusy(true);
			try {
				const feature = await findSegment({ projectUuid, point: event.coordinate });
				const box = feature?.properties?.bbox;
				if (box) addMissed(box);
				else
					reportError(
						new Error('Nothing to outline there: drag a box around it instead.')
					);
			} catch (error) {
				reportError(error);
			} finally {
				setAddingBusy(false);
			}
		};
		mapObject.on('click', handleClick);
		return () => {
			mapObject.removeInteraction(drag);
			mapObject.un('click', handleClick);
			viewport.style.cursor = '';
		};
	}, [mapObject, active, adding, addMissed, projectUuid, reportError]);

	useEffect(() => {
		if (!mapObject || !active || adding) return undefined;
		const viewport = mapObject.getViewport();
		// The box a click means where boxes overlap (angled cars, a car by a van): the one whose centre is
		// nearest the click, relative to its size; about as near, the smaller one
		const resultAt = (pixel, [x, y]) => {
			let best;
			let bestReach = Infinity;
			mapObject.forEachFeatureAtPixel(
				pixel,
				feature => {
					const result = feature.get('result');
					if (!result?.bbox) return undefined;
					const [x0, y0, x1, y1] = result.bbox;
					const reach = Math.max(
						Math.abs(x - (x0 + x1) / 2) / Math.max((x1 - x0) / 2, 1e-9),
						Math.abs(y - (y0 + y1) / 2) / Math.max((y1 - y0) / 2, 1e-9)
					);
					const nearer = reach < bestReach - 0.1;
					const asNear = Math.abs(reach - bestReach) <= 0.1;
					if (!best || nearer || (asNear && boxArea(result.bbox) < boxArea(best.bbox))) {
						best = result;
						bestReach = Math.min(reach, bestReach);
					}
					return undefined;
				},
				{ layerFilter: layer => layer === resultsLayer, hitTolerance: 2 }
			);
			return best;
		};
		const squareAt = ([x, y]) =>
			planTiles.find(
				({ core }) => core[0] <= x && x < core[2] && core[1] <= y && y < core[3]
			);
		const onCheckbox = (cell, [px, py]) => {
			if (!cell || search?.running || search?.tiles?.[cell.i]?.state === 'queued')
				return false;
			// The same size the grid draws it at; none when zoomed out too far to show squares
			const size = checkboxSize(
				(cell.core[2] - cell.core[0]) / mapObject.getView().getResolution()
			);
			if (!size) return false;
			const [cx, cy] = checkboxCentre(
				mapObject.getPixelFromCoordinate([cell.core[2], cell.core[3]]),
				size
			);
			const reach = size / 2 + CHECKBOX.inset / 2;
			return Math.abs(px - cx) <= reach && Math.abs(py - cy) <= reach;
		};
		// With the heatmap showing, a click is "this one too": SAM 3 outlines the object under it and it
		// is added, unless it was found already. A click alone does not say how big the object is (one
		// car, or the row of them), so it goes with a box of the examples' size around it (from a dot,
		// the dot's box)
		const pick = async event => {
			const spot = mapObject.forEachFeatureAtPixel(
				event.pixel,
				feature => feature.get('bbox') ?? undefined,
				{ hitTolerance: 6 }
			);
			const [x, y] = event.coordinate;
			const [w, h] = (pickOnClick.size ?? [0, 0]).map(side => (side * PICK_BOX_SCALE) / 2);
			const around = w && h ? [x - w, y - h, x + w, y + h] : undefined;
			setAddingBusy(true);
			viewport.style.cursor = 'progress';
			try {
				const feature = await findSegment({
					projectUuid,
					point: event.coordinate,
					box: spot ?? around,
				});
				const box = feature?.properties?.bbox;
				if (!box) {
					reportError(new Error('Nothing to outline there.'));
					return;
				}
				const known = results.find(r => !r.preview && r.bbox && sameObject(r.bbox, box));
				if (known && holds(box, known.bbox)) {
					// The search had only a part of it (a van's end): the whole object replaces it
					if (effectiveDecisions[known.id] !== 'rejected') toggleRemoved(known);
					addFound(box, pickOnClick, 'click');
				} else if (known) {
					setSelectedId(known.id);
					if (effectiveDecisions[known.id] === 'rejected') toggleRemoved(known);
				} else {
					addFound(box, pickOnClick, 'click');
				}
			} catch (error) {
				reportError(error);
			} finally {
				setAddingBusy(false);
				viewport.style.cursor = 'crosshair';
			}
		};
		const handleClick = event => {
			const cell = squareAt(event.coordinate);
			if (onCheckbox(cell, event.pixel)) {
				setSelectedTile(cell.i);
				reviewTile(cell.i, VERDICT.TICKED);
				return;
			}
			const hit = resultAt(event.pixel, event.coordinate);
			if (hit && !hit.preview) {
				// Crossing a box out (or back in) leaves the selected square where it is
				toggleRemoved(hit);
				return;
			}
			setSelectedId(null);
			if (pickOnClick) pick(event);
			else setSelectedTile(cell?.i ?? null);
		};
		const handleMove = event => {
			if (event.dragging) return;
			const hit = resultAt(event.pixel, event.coordinate);
			setHoveredId(hit?.id ?? null);
			const clickable = Boolean(hit) || onCheckbox(squareAt(event.coordinate), event.pixel);
			viewport.style.cursor = clickable ? 'pointer' : pickOnClick ? 'crosshair' : '';
		};
		mapObject.on('click', handleClick);
		mapObject.on('pointermove', handleMove);
		return () => {
			mapObject.un('click', handleClick);
			mapObject.un('pointermove', handleMove);
			viewport.style.cursor = '';
		};
	}, [
		mapObject,
		active,
		adding,
		pickOnClick,
		projectUuid,
		results,
		effectiveDecisions,
		addFound,
		reportError,
		resultsLayer,
		planTiles,
		search?.running,
		search?.tiles,
		reviewTile,
		toggleRemoved,
	]);

	const zoomTo = useCallback(
		result => {
			setSelectedId(result.id);
			if (result.bbox) {
				mapObject?.getView().fit(result.bbox, {
					padding: [120, 420, 120, 120],
					maxZoom: 22,
					duration: 400,
				});
				runSegment({ box: result.bbox });
			}
		},
		[mapObject, runSegment]
	);

	const forget = useCallback(() => {
		generationRef.current += 1;
		abortRef.current?.abort();
		outlineAbortRef.current?.abort();
		setRun(null);
		dispatchSearch({ type: 'reset' });
		setDecisions({});
		setOutline(null);
		setOutlines({});
		setOutlining(null);
		setSelectedId(null);
		setTileReview({});
		setSelectedTile(null);
		setAdding(null);
	}, [setRun]);
	// Clear the results and the search behind them: a running one stops on the server
	const clear = useCallback(() => {
		if (runRef.current)
			clearFindRun({ projectUuid, runUuid: runRef.current }).catch(() => null);
		forget();
	}, [projectUuid, forget]);

	return {
		session: session.data,
		// "Add missed"
		adding,
		addingBusy,
		startAdding,
		stopAdding,
		// When the user (or opening the panel) asked compute to start, for "Starting · 0:42"
		startRequestedAt,
		startSession: requestStart,
		// A start waits for compute to finish stopping
		startQueued,
		stopSession: stopSession.mutate,
		tileReview,
		selectedTile,
		reviewTile,
		// The size filter: per object its boxes' sizes, range and hidden count; set a range by hand
		sizeGroups,
		setSizeRange: (group, range) => setSizeRanges(current => ({ ...current, [group]: range })),
		tickableInView,
		tickAllInView,
		untickAll,
		outline,
		outlines,
		outlining,
		outlineAll,
		similar,
		query,
		model,
		stop,
		search,
		progress: searchProgress(search),
		// The run on the server the panel shows, and saving its results to the project
		runUuid,
		saveResults: ({ name, collectionUuid } = {}) =>
			save.mutateAsync({ runUuid: runRef.current, name, collectionUuid }),
		saving: save.isPending,
		saveError: save.isError && save.variables?.runUuid === runUuid ? save.error : null,
		results,
		decisions: effectiveDecisions,
		toggleRemoved,
		// Keep one box by hand; doing it again undoes it
		keep: id =>
			setDecisions(current => ({
				...current,
				[id]: current[id] === 'accepted' ? undefined : 'accepted',
			})),
		hoveredId,
		setHoveredId,
		selectedId,
		segment,
		zoomTo,
		clear,
		// Closing the panel: the page lets go of the search, which runs on; opening Find picks it up
		detach: forget,
	};
};
